/**
 * Shared cart store.
 *
 * The mini-cart drawer owns the network calls (it holds the localized cart
 * endpoints). This store is the read model every other surface binds to:
 * product cards, the product page, the sticky mobile bar, quick view. They
 * ask "how many of this do I already have" and mutate through the same two
 * global functions the drawer exposes, so there is exactly one cart truth.
 *
 * Wiring:
 *   drawer merge()  -> Alpine.store('cart').set(payload)
 *   window.addToCart(productId, qty, variantId)  -> add a line
 *   window.setCartLineQty(lineId, qty)           -> update / remove a line
 */
export function registerCartStore(Alpine) {
    Alpine.store('cart', {
        count: 0,
        lines: [],
        // Keyed by cart-line id (or an add key) while a request is in flight,
        // so a fast double tap cannot fire two conflicting writes.
        busy: {},

        set(payload) {
            this.count = payload.count ?? 0;
            this.lines = payload.lines ?? [];
        },

        /**
         * Cart lines matching a target.
         * @param {{product?: string, variant?: string, bundle?: string}} target
         */
        linesFor(target = {}) {
            return this.lines.filter((line) => {
                if (target.bundle) return line.bundle_id === target.bundle;
                if (!target.product || line.product_id !== target.product) return false;
                if (target.variant) return line.variant_id === target.variant;
                return true;
            });
        },

        /** Total units of this product (or variant) already in the cart. */
        qty(target = {}) {
            return this.linesFor(target).reduce((sum, line) => sum + (line.qty || 0), 0);
        },

        /**
         * A stepper can only edit a target that resolves to ONE cart line.
         * Two variants of the same product = ambiguous, the card falls back to
         * a "manage in cart" chip and the drawer stays the editor.
         */
        singleLine(target = {}) {
            const lines = this.linesFor(target);
            return lines.length === 1 ? lines[0] : null;
        },

        isEditable(target = {}) {
            return this.singleLine(target) !== null;
        },

        isBusy(target = {}) {
            const line = this.singleLine(target);
            const addKey = this.addKey(target);
            return Boolean(this.busy[addKey] || (line && this.busy[line.id]));
        },

        addKey(target = {}) {
            return 'add:' + (target.bundle || target.product || '') + ':' + (target.variant || '');
        },

        async add(target = {}, qty = 1) {
            const key = this.addKey(target);
            if (this.busy[key]) return;
            this.busy[key] = true;
            try {
                if (target.bundle) {
                    if (window.addBundleToCart) await window.addBundleToCart(target.bundle, qty);
                } else if (window.addToCart) {
                    await window.addToCart(target.product, qty, target.variant || null);
                }
            } finally {
                this.busy[key] = false;
            }
        },

        async increment(target = {}) {
            const line = this.singleLine(target);
            if (!line) return this.add(target, 1);
            return this.setLineQty(line, line.qty + 1);
        },

        async decrement(target = {}) {
            const line = this.singleLine(target);
            if (!line) return;
            return this.setLineQty(line, line.qty - 1);
        },

        /**
         * Optimistic: the number moves on tap, the server response replaces the
         * whole line set a moment later. A failed request resyncs from the
         * server inside the drawer, so a wrong optimistic value cannot stick.
         */
        async setLineQty(line, qty) {
            if (this.busy[line.id]) return;
            this.busy[line.id] = true;

            const next = Math.max(0, qty);
            this.lines = next === 0
                ? this.lines.filter((l) => l.id !== line.id)
                : this.lines.map((l) => (l.id === line.id ? { ...l, qty: next } : l));
            this.count = this.lines.reduce((sum, l) => sum + (l.qty || 0), 0);

            try {
                if (window.setCartLineQty) await window.setCartLineQty(line.id, next);
            } finally {
                this.busy[line.id] = false;
            }
        },

        openDrawer() {
            window.dispatchEvent(new CustomEvent('cart-open'));
        },
    });
}
